import React from 'react';
import { useSectionControl } from '../contexts/SectionControlContext';
import { getSectionSizeClasses, getSectionStretch, isSpacerSection } from '../utils/sectionWidth';
import EditModeSectionLabel from './EditModeSectionLabel';

/**
 * Wrapper for individual sections.
 * In non-grid (stacked) layouts: reads width/height config and applies
 * responsive max-width / min-height Tailwind classes.
 * In GridEngine layouts: skips size classes (GridEngine handles cell sizing
 * via inline gridColumn/gridRow styles and the sectionConfigs bridge).
 *
 * When `stretch` is enabled (default), the wrapper and its children
 * expand to fill the grid cell height so side-by-side sections match
 * the tallest sibling.
 *
 * Unknown section types are auto-registered by EditModeSectionLabel
 * so every rendered section gets admin controls.
 */
export default function SectionItem({
  sectionType,
  editMode,
  label,
  className = '',
  style,
  children,
  insideGridEngine = false,
  noContentVisibility = false, // hero sections set this to true so they render immediately
}) {
  const sectionControl = useSectionControl();
  const section = sectionControl?.getSectionByType?.(sectionType);
  const shouldStretch = isSpacerSection(sectionType) ? false : getSectionStretch(section);
  const sizeClasses = insideGridEngine ? '' : getSectionSizeClasses(section, { stretch: shouldStretch });
  // Dropdown sections (like purchase_partner_selector) must not use content-visibility:auto
  // and must have elevated z-index so absolute dropdowns/popovers never render underneath following sections.
  const isDropdownSection = sectionType === 'purchase_partner_selector';
  const noCv = noContentVisibility || isDropdownSection;
  const cvClass = !noCv ? ' cv-auto' : '';
  const zClass = isDropdownSection ? ' z-30' : '';

  if (!children && !editMode) return null;

  return (
    <div
      className={`relative min-w-0 ${zClass} ${shouldStretch ? 'flex flex-col' : ''}${cvClass} ${sizeClasses} ${className}`.trim()}
      style={style}
      data-section={sectionType}
    >
      <EditModeSectionLabel
        sectionType={sectionType}
        editMode={editMode}
        label={label}
      />
      {shouldStretch ? (
        <div className="flex-1 min-h-0 flex flex-col [&>*]:flex-1">{children}</div>
      ) : (
        children
      )}
    </div>
  );
}
